import React, { useState, useEffect } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const ViewFileModal = ({ isOpen, onClose, file }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fileContent, setFileContent] = useState(null); // Stores text/csv data

  // PDFs are rendered with @react-pdf-viewer (instead of the native iframe
  // viewer) so that link annotations are real DOM anchors — which lets the
  // app-wide handler force external links to open in a new tab.
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  useEffect(() => {
    if (isOpen && file) {
      setLoading(true);
      setError(false);
      setFileContent(null);

      const isTextBased = ["csv", "txt", "json"].includes(file.file_type);

      if (isTextBased) {
        // 1. Handle Text/CSV Files (Fetch directly)
        fetch(file.url)
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch file");
            return res.text();
          })
          .then((text) => {
            setFileContent(text);
            setLoading(false);
          })
          .catch((err) => {
            console.error("Error fetching text file:", err);
            setError(true);
            setLoading(false);
          });
      } else {
        // 2. Handle Office/PDF/Images (Iframe/Img)
        // Reset loading state. For Office Viewer, we use a timeout because 
        // cross-origin iframe onLoad events are sometimes blocked or unreliable.
        if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(file.file_type)) {
             // Give the Microsoft viewer a moment to initialize
             setTimeout(() => setLoading(false), 1500); 
        }
      }
    }
  }, [isOpen, file]);

  if (!isOpen || !file) return null;

  const handleDownloadFile = async () => {
    if (!file?.url) return;
    const fileName = file.name || "document";
    try {
      const res = await fetch(file.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error:", err);
      // Could not fetch the file for a renamed download (e.g. CORS) —
      // fall back to opening it in a separate tab.
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  const getFileViewUrl = () => {
    if (!file.url) return null;

    // PDF -> Native Browser Viewer
    if (file.file_type === "pdf") {
      return file.url;
    }

    // Office Files -> Microsoft Office Web Viewer
    // (More reliable for docx/xlsx than Google Viewer)
    const officeTypes = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
    if (officeTypes.includes(file.file_type)) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
    }

    // Images -> Native
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(file.file_type)) {
      return file.url;
    }

    return null;
  };

  // Helper: Render CSV as a Table
  const renderCsvTable = (csvText) => {
    if (!csvText) return null;
    const rows = csvText.split(/\r?\n/).filter((row) => row.trim() !== "");
    
    return (
      <div className="overflow-auto max-h-full p-4">
        <table className="min-w-full border-collapse border border-gray-200 bg-white text-sm text-left shadow-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
               {rows[0]?.split(",").map((header, i) => (
                 <th key={i} className="px-4 py-2 font-semibold text-gray-700 whitespace-nowrap border border-gray-200">
                   {header}
                 </th>
               ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(1).map((row, rowIndex) => {
              const cells = row.split(","); 
              return (
                <tr
                  key={rowIndex}
                  className="hover:bg-gray-50 border-b border-gray-100"
                >
                  {cells.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="px-4 py-2 border border-gray-200 whitespace-nowrap text-gray-700"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper: Render Plain Text/JSON
  const renderTextContent = (text) => (
    <div className="p-6 overflow-auto h-full bg-white">
      <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 bg-gray-50 p-4 rounded-lg border border-gray-200">
        {text}
      </pre>
    </div>
  );

  const viewUrl = getFileViewUrl();
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(file.file_type);
  const isPdf = file.file_type === "pdf";
  const isCsv = file.file_type === "csv";
  const isText = file.file_type === "txt" || file.file_type === "json";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10000] font-outfit backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-[60vw] h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Dynamic Icon based on file type */}
            <div className="p-2 bg-gray-100 rounded-lg">
                <img
                src={
                    file.file_type === "pdf" ? "/pdff.svg"
                    : ["xlsx", "xls"].includes(file.file_type) ? "/Xls.svg"
                    : ["docx", "doc"].includes(file.file_type) ? "/docX.svg"
                    : file.file_type === "csv" ? "/CSV.svg"
                    : "/TXT.svg"
                }
                className="w-5 h-5 flex-shrink-0"
                alt="file icon"
                />
            </div>
            <div className="flex flex-col">
                <h2 className="text-lg font-semibold text-gray-900 truncate max-w-md" title={file.name}>
                {file.name}
                </h2>
                <span className="text-xs text-gray-500">Preview Mode</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 relative bg-gray-50 overflow-hidden">
          {/* Loading Spinner */}
          {loading && !fileContent && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-[#C95EBE] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-gray-600 font-medium animate-pulse">
                  Loading document...
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white">
              <div className="flex flex-col items-center gap-4 max-w-md px-6 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    Unable to preview this file
                  </p>
                  <p className="text-sm text-gray-600">
                    {isCsv || isText 
                      ? "Could not load the text content. Access might be restricted."
                      : "The file format might not be supported for preview."}
                  </p>
                </div>
                <button
                  onClick={handleDownloadFile}
                  className="px-6 py-2 bg-[#C95EBE] text-white rounded-lg font-medium hover:bg-[#B04EA8] transition-colors shadow-sm"
                >
                  Download File Instead
                </button>
              </div>
            </div>
          ) : (
            // Success Render
            <>
              {isCsv && fileContent && renderCsvTable(fileContent)}
              {isText && fileContent && renderTextContent(fileContent)}
              
              {!isCsv && !isText && viewUrl && (
                <>
                  {isImage ? (
                    <div className="w-full h-full flex items-center justify-center p-4 overflow-auto bg-gray-100">
                      <img
                        src={viewUrl}
                        alt={file.name}
                        className="max-w-full max-h-full object-contain shadow-lg rounded"
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                      />
                    </div>
                  ) : isPdf ? (
                    <div className="w-full h-full overflow-hidden">
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer
                          fileUrl={file.url}
                          plugins={[defaultLayoutPluginInstance]}
                          onDocumentLoad={handleIframeLoad}
                        />
                      </Worker>
                    </div>
                  ) : (
                    <iframe
                      src={viewUrl}
                      className="w-full h-full border-0"
                      title={file.name}
                      onLoad={handleIframeLoad}
                      onError={handleIframeError}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="bg-gray-100 px-2 py-1 rounded">Size: {file.size}</span>
            <span className="text-gray-300">|</span>
            <span>Uploaded: {file.uploaded_at}</span>
          </div>
          <button
            onClick={handleDownloadFile}
            className="px-4 py-2 bg-[#18181B] text-white rounded-lg font-medium hover:bg-gray-800 transition-colors flex items-center gap-2 shadow-sm text-[14px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewFileModal;
